import { useEffect, useState } from 'react';
import NProgress from 'nprogress';

/**
 * Branded route-loading screen.
 *
 * Mirrors the static pre-React loader baked into public/index.html (the
 * `.kload` block) so the two loading phases — main bundle download, then
 * lazy route-chunk download — look like one continuous, on-brand experience
 * instead of a logo screen followed by a bare spinner.
 *
 * The spinner is delayed ~280ms so fast / cached chunk loads don't flash a
 * loading screen, which keeps navigation feeling smooth.
 */

const LoaderStyles = () => (
  <style>{`
    @keyframes sload-spin { to { transform: rotate(360deg); } }
    @keyframes sload-pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.08); } }
    @keyframes sload-fade { 0%, 100% { opacity: .55; } 50% { opacity: 1; } }
    .sload {
      position: fixed; inset: 0; z-index: 9999;
      display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 26px;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: linear-gradient(135deg, #fcdfe9 0%, #fdeadd 36%, #efe8fb 70%, #f5f1fd 100%);
    }
    .sload-logo { position: relative; width: 96px; height: 96px; display: grid; place-items: center; }
    .sload-ring {
      position: absolute; inset: 0; border-radius: 50%;
      background: conic-gradient(from 0deg, #6c5ce7, #ab9bed, #f7b4cf, #6c5ce7);
      -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 6px), #000 0);
      mask: radial-gradient(farthest-side, transparent calc(100% - 6px), #000 0);
      animation: sload-spin 1.1s linear infinite;
    }
    .sload-mark {
      width: 54px; height: 54px; border-radius: 15px; object-fit: cover;
      box-shadow: 0 10px 28px -8px rgba(108, 92, 231, .6);
      animation: sload-pulse 1.6s ease-in-out infinite;
    }
    .sload-text { font-size: 14px; font-weight: 600; letter-spacing: .3px; color: #54507a; animation: sload-fade 1.6s ease-in-out infinite; }

    /* dark mode */
    html.dark .sload { background: linear-gradient(140deg, #0e0f14 0%, #13141d 55%, #0e0f14 100%); }
    html.dark .sload-text { color: #aeb3c0; }
  `}</style>
);

function SuspenseLoader() {
  // Delay showing the loader so fast / cached chunk loads don't flash a
  // loading screen — this is what makes navigation feel smooth.
  const [show, setShow] = useState(false);

  useEffect(() => {
    NProgress.start();
    const timer = setTimeout(() => setShow(true), 280);

    return () => {
      clearTimeout(timer);
      NProgress.done();
    };
  }, []);

  return (
    <div
      className="sload"
      style={{
        pointerEvents: 'none',
        opacity: show ? 1 : 0,
        transition: 'opacity .3s ease'
      }}
    >
      <LoaderStyles />
      <div className="sload-logo">
        <span className="sload-ring" />
        <img src="/favicon.ico" className="sload-mark" alt="Kymera" />
      </div>
      <div className="sload-text">Loading your workspace…</div>
    </div>
  );
}

export default SuspenseLoader;
